// Client-side Google Sheets API v4 reads — same pattern as Candidate
// Tracker's src/utils/sheetsApi.js (direct fetch, no backend server).
// Unlike Candidate Tracker, TrackWithTux always reads the same three named
// tabs from one fixed spreadsheet, so this fetches all of them in a single
// batchGet call instead of resolving a tab title from a gid.

// Pulls a sheet ID out of a pasted full Google Sheets URL, or passes through
// a bare ID if the URL pattern doesn't match.
export function extractSheetId(input) {
  const m = input.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (m) return m[1];
  return input.trim();
}

function rowsToObjects(values) {
  if (!values || values.length === 0) return [];
  const [headers, ...rows] = values;
  return rows.map((row) => {
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = row[i] !== undefined ? row[i] : '';
    });
    return obj;
  });
}

function toBool(v) {
  if (typeof v === 'boolean') return v;
  return String(v).trim().toUpperCase() === 'TRUE';
}

async function parseErrorDetail(res) {
  let detail = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (body && body.error && body.error.message) detail += `: ${body.error.message}`;
  } catch (e) {
    // ignore, keep the basic status detail
  }
  return detail;
}

// Fetches Users, Timelogs, TZ Offsets, Leaves, and LeaveBalances in one call
// and shapes them to match the shape AppContext expects. Leaves/LeaveBalances
// keep their raw snake_case sheet column names (same convention as
// timelogs/users) — any camelCase reshaping for display happens in
// utils/leaves.js, not here.
export async function fetchTrackWithTuxData(sheetIdOrUrl, apiKey) {
  const sheetId = extractSheetId(sheetIdOrUrl);
  const ranges = [
    'Users',
    'Timelogs',
    'TZ Offsets',
    'Leaves',
    'LeaveBalances',
    'Announcements',
    'Holidays',
    'HolidayLists',
    'HolidayListItems',
    'Clients',
    'ClientStaff',
    'ClientRoles',
    'RoleCriteria',
    'Applicants',
    'ApplicantCriteriaAnswers',
  ];
  const params = new URLSearchParams();
  ranges.forEach((r) => params.append('ranges', r));
  params.append('key', apiKey);

  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values:batchGet?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const detail = await parseErrorDetail(res);
    const err = new Error('sheets-api-error');
    err.detail = detail;
    throw err;
  }

  const data = await res.json();
  const [
    usersRange,
    timelogsRange,
    tzRange,
    leavesRange,
    leaveBalancesRange,
    announcementsRange,
    holidaysRange,
    holidayListsRange,
    holidayListItemsRange,
    clientsRange,
    clientStaffRange,
    clientRolesRange,
    roleCriteriaRange,
    applicantsRange,
    applicantCriteriaAnswersRange,
  ] = data.valueRanges || [];

  const users = rowsToObjects(usersRange && usersRange.values).map((u) => ({
    ...u,
    isAdmin: toBool(u.isAdmin),
  }));

  const timelogs = rowsToObjects(timelogsRange && timelogsRange.values).map((l) => ({
    ...l,
    isIn: toBool(l.isIn),
    is_manual: toBool(l.is_manual),
    notes_edited: toBool(l.notes_edited),
  }));

  const tzOffsets = {};
  rowsToObjects(tzRange && tzRange.values).forEach((row) => {
    tzOffsets[row.timezone] = Number(row.utc_offset_hours);
  });

  const leaves = rowsToObjects(leavesRange && leavesRange.values).map((l) => ({
    ...l,
    days: Number(l.days) || 0,
  }));

  const leaveBalances = rowsToObjects(leaveBalancesRange && leaveBalancesRange.values).map((b) => ({
    ...b,
    credits_total: Number(b.credits_total) || 0,
    credits_remaining: Number(b.credits_remaining) || 0,
  }));

  const announcements = rowsToObjects(announcementsRange && announcementsRange.values).map((a) => ({
    ...a,
    month: a.month === '' ? '' : Number(a.month),
    day: a.day === '' ? '' : Number(a.day),
    interval_days: a.interval_days === '' ? '' : Number(a.interval_days),
    lead_days: Number(a.lead_days) || 0,
    active: toBool(a.active),
  }));

  const holidays = rowsToObjects(holidaysRange && holidaysRange.values).map((h) => ({
    ...h,
    month: Number(h.month) || 0,
    day: Number(h.day) || 0,
  }));

  const holidayLists = rowsToObjects(holidayListsRange && holidayListsRange.values);

  const holidayListItems = rowsToObjects(holidayListItemsRange && holidayListItemsRange.values);

  const clients = rowsToObjects(clientsRange && clientsRange.values);

  const clientStaff = rowsToObjects(clientStaffRange && clientStaffRange.values);

  const roles = rowsToObjects(clientRolesRange && clientRolesRange.values);

  const roleCriteria = rowsToObjects(roleCriteriaRange && roleCriteriaRange.values).map((c) => ({
    ...c,
    sort_order: Number(c.sort_order) || 0,
  }));

  const applicants = rowsToObjects(applicantsRange && applicantsRange.values);

  const applicantAnswers = rowsToObjects(applicantCriteriaAnswersRange && applicantCriteriaAnswersRange.values);

  return {
    users,
    timelogs,
    tzOffsets,
    leaves,
    leaveBalances,
    announcements,
    holidays,
    holidayLists,
    holidayListItems,
    clients,
    clientStaff,
    roles,
    roleCriteria,
    applicants,
    applicantAnswers,
  };
}

// Next log_id in the same logNNN sequence as the sheet, based on the
// highest numeric suffix currently loaded.
export function nextLogId(existingLogs) {
  const nums = existingLogs
    .map((l) => parseInt(String(l.log_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `log${String(max + 1).padStart(3, '0')}`;
}

// Next leave_id in the same leaveNNN sequence as the sheet, same pattern as
// nextLogId.
export function nextLeaveId(existingLeaves) {
  const nums = existingLeaves
    .map((l) => parseInt(String(l.leave_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `leave${String(max + 1).padStart(3, '0')}`;
}

// Next announcement_id in the same annNNN sequence as the sheet, same
// pattern as nextLogId/nextLeaveId.
export function nextAnnouncementId(existingAnnouncements) {
  const nums = existingAnnouncements
    .map((a) => parseInt(String(a.announcement_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `ann${String(max + 1).padStart(3, '0')}`;
}

// Next holiday_id in the same holNNN sequence as the sheet, same pattern as
// nextLogId/nextLeaveId/nextAnnouncementId.
export function nextHolidayId(existingHolidays) {
  const nums = existingHolidays
    .map((h) => parseInt(String(h.holiday_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `hol${String(max + 1).padStart(3, '0')}`;
}

// Next list_id in the same hlistNNN sequence as the sheet.
export function nextHolidayListId(existingHolidayLists) {
  const nums = existingHolidayLists
    .map((l) => parseInt(String(l.list_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `hlist${String(max + 1).padStart(3, '0')}`;
}

// Next client_id in the same cliNNN sequence as the sheet.
export function nextClientId(existingClients) {
  const nums = existingClients
    .map((c) => parseInt(String(c.client_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `cli${String(max + 1).padStart(3, '0')}`;
}

// Next role_id in the same roleNNN sequence as the sheet.
export function nextRoleId(existingRoles) {
  const nums = existingRoles
    .map((r) => parseInt(String(r.role_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `role${String(max + 1).padStart(3, '0')}`;
}

// Next criteria_id in the same critNNN sequence as the sheet.
export function nextCriteriaId(existingRoleCriteria) {
  const nums = existingRoleCriteria
    .map((c) => parseInt(String(c.criteria_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `crit${String(max + 1).padStart(3, '0')}`;
}

// Next applicant_id in the same appNNN sequence as the sheet.
export function nextApplicantId(existingApplicants) {
  const nums = existingApplicants
    .map((a) => parseInt(String(a.applicant_id).replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `app${String(max + 1).padStart(3, '0')}`;
}

// Next uuid for a new employee, matching the existing tux-<name>-NNN
// convention (e.g. "tux-tracey-001") — mirrors Code.gs's nextEmployeeUuid_
// for the local-fallback (no REACT_APP_APPS_SCRIPT_URL) dev path.
export function nextEmployeeUuid(existingUsers, name) {
  const existing = new Set(existingUsers.map((u) => u.uuid));
  const slug = String(name || '').trim().toLowerCase().split(/\s+/)[0].replace(/[^a-z]/g, '') || 'emp';
  let n = 1;
  let candidate;
  do {
    candidate = `tux-${slug}-${String(n).padStart(3, '0')}`;
    n += 1;
  } while (existing.has(candidate));
  return candidate;
}
